import { loadJson, saveJson } from '@/features/kitchen/storage/persist'
import { buildPlanCodeFallback } from '@/features/kitchen/productionCodes'

export const PRODUCTION_STORAGE_KEY = 'superoti.kitchen.production'

export type SessionStatus = 'in_progress' | 'draft' | 'posted'

export type ProductionShift = 'MORNING' | 'NOON' | 'EVENING'

export type ProductionSessionRecord = {
  ref: string
  title: string
  updatedAt: string
  status: SessionStatus
  shift?: ProductionShift
  note?: string
}

export type ProductionLineRecord = {
  id: string
  productName: string
  source: 'plan' | 'off_plan'
  planQty: number | null
  qty: number
}

export type ProductionPersistedState = {
  sessions: ProductionSessionRecord[]
  sessionLinesByRef: Record<string, ProductionLineRecord[]>
}

const PLAN_CATALOG = [
  { sku: 'Roti coklat', plan: 100 },
  { sku: 'Roti keju', plan: 80 },
  { sku: 'Donat gula', plan: 50 },
]

export const DEFAULT_PRODUCTION_STATE: ProductionPersistedState = {
  sessions: [
    {
      ref: 'PRD-DAPUR-A-20260623-01',
      title: '23/06/26',
      shift: 'MORNING',
      updatedAt: 'Diperbarui 14:20',
      status: 'in_progress',
    },
    {
      ref: 'PRD-DAPUR-A-20260623-02',
      title: '23/06/26',
      shift: 'EVENING',
      updatedAt: 'Diperbarui 13:05',
      status: 'draft',
    },
    {
      ref: 'PRD-DAPUR-A-20260622-04',
      title: '22/06/26',
      shift: 'NOON',
      updatedAt: 'Sudah masuk stok 22/06 17:42',
      status: 'posted',
    },
  ],
  sessionLinesByRef: {
    'PRD-DAPUR-A-20260623-01': [
      { id: 'l1', productName: 'Roti coklat', source: 'plan', planQty: 100, qty: 35 },
      { id: 'l2', productName: 'Roti keju', source: 'plan', planQty: 80, qty: 40 },
    ],
    'PRD-DAPUR-A-20260623-02': [
      { id: 'l4', productName: 'Donat gula', source: 'plan', planQty: 50, qty: 10 },
    ],
  },
}

export function loadProductionState(): ProductionPersistedState {
  return loadJson<ProductionPersistedState>(PRODUCTION_STORAGE_KEY) ?? DEFAULT_PRODUCTION_STATE
}

export function saveProductionState(state: ProductionPersistedState): void {
  saveJson(PRODUCTION_STORAGE_KEY, state)
}

function computePlanActual(state: ProductionPersistedState): { planTotal: number; actualTotal: number } {
  const actualBySku = new Map<string, number>()
  const planSkus = new Set(PLAN_CATALOG.map((item) => item.sku))

  for (const session of state.sessions) {
    if (session.status === 'draft') continue
    for (const line of state.sessionLinesByRef[session.ref] ?? []) {
      if (line.qty <= 0 || !line.productName.trim()) continue
      const name = line.productName.trim()
      actualBySku.set(name, (actualBySku.get(name) ?? 0) + line.qty)
    }
  }

  let planTotal = 0
  let actualTotal = 0

  for (const item of PLAN_CATALOG) {
    planTotal += item.plan
    actualTotal += actualBySku.get(item.sku) ?? 0
  }

  for (const [sku, actual] of actualBySku) {
    if (!planSkus.has(sku)) actualTotal += actual
  }

  return { planTotal, actualTotal }
}

export function getProductionSummary() {
  const state = loadProductionState()
  const openSessions = state.sessions.filter((s) => s.status !== 'posted')
  const inProgress = openSessions.filter((s) => s.status === 'in_progress').length
  const drafts = openSessions.filter((s) => s.status === 'draft').length
  const { planTotal, actualTotal } = computePlanActual(state)
  const planPct = planTotal > 0 ? Math.min(100, Math.round((actualTotal / planTotal) * 100)) : 0

  return {
    openSessionCount: openSessions.length,
    inProgress,
    drafts,
    planPct,
    planTotal,
    actualTotal,
    planCode: buildPlanCodeFallback(new Date().toISOString().slice(0, 10), 'DAPUR-A'),
  }
}
